/* eslint-disable @typescript-eslint/no-explicit-any */
declare interface Fn<T = any, R = T> {
    (...arg: T[]): R
}

function _registerEvent(target: any, eventType: string, cb: Fn) {
    if (target.addEventListener) {
        target.addEventListener(eventType, cb)
        return {
            remove: function() {
                target.removeEventListener(eventType, cb)
            },
        }
    } else {
        target.attachEvent(eventType, cb)
        return {
            remove: function() {
                target.detachEvent(eventType, cb)
            },
        }
    }
}

function _createHiddenIframe(target: HTMLElement, uri: string): HTMLIFrameElement {
    const iframe = document.createElement('iframe')
    iframe.src = uri
    iframe.id = 'hiddenIframe'
    iframe.style.display = 'none'
    target.appendChild(iframe)

    return iframe
}

function openUriWithHiddenFrame(uri: string, failCb: Fn, successCb: Fn) {
    let handler
    const timeout = setTimeout(function() {
        failCb()
        handler.remove()
    }, 1000)

    let iframe = document.querySelector<HTMLIFrameElement>('#hiddenIframe')
    if (!iframe) {
        iframe = _createHiddenIframe(document.body, 'about:blank')
    }
    function onBlur() {
        clearTimeout(timeout)
        handler.remove()
        successCb()
    }
    handler = _registerEvent(window, 'blur', onBlur)

    iframe.contentWindow && (iframe.contentWindow.location.href = uri)
}

function openUriWithTimeoutHack(uri: string, failCb: Fn, successCb: Fn) {
    let handler
    const timeout = setTimeout(function() {
        failCb()
        handler.remove()
    }, 1000)
    //handle page running in an iframe (blur must be registered with top level window)
    let target = window
    while (target != target.parent) {
        target = target.parent
    }
    function onBlur() {
        clearTimeout(timeout)
        handler.remove()
        successCb()
    }
    handler = _registerEvent(target, 'blur', onBlur)

    window.location.href = uri
}

function openUriUsingFirefox(uri: string, failCb: Fn, successCb: Fn) {
    let iframe = document.querySelector<HTMLIFrameElement>('#hiddenIframe')

    if (!iframe) {
        iframe = _createHiddenIframe(document.body, 'about:blank')
    }

    try {
        iframe.contentWindow && (iframe.contentWindow.location.href = uri)
        successCb()
    } catch (e) {
        if (e.name == 'NS_ERROR_UNKNOWN_PROTOCOL') {
            failCb()
        }
    }
}
function openUriUsingIE10InWindows7(uri: string, failCb: Fn, successCb: Fn) {
    const timeout = setTimeout(failCb, 1000)
    window.addEventListener('blur', function() {
        clearTimeout(timeout)
        successCb()
    })

    let iframe = document.querySelector<HTMLIFrameElement>('#hiddenIframe')
    if (!iframe) {
        iframe = _createHiddenIframe(document.body, 'about:blank')
    }
    try {
        iframe.contentWindow && (iframe.contentWindow.location.href = uri)
    } catch (e) {
        failCb()
        clearTimeout(timeout)
    }
}

function openUriInNewWindowHack(uri: string, failCb: Fn, successCb: Fn) {
    const myWindow = window.open('', '', 'width=0,height=0')
    if (!myWindow) {
        return
    }

    myWindow.document.write("<iframe src='" + uri + "'></iframe>")

    setTimeout(function() {
        try {
            myWindow.location.href
            myWindow.setTimeout('window.close()', 1000)
            successCb()
        } catch (e) {
            myWindow.close()
            failCb()
        }
    }, 1000)
}

function openUriWithMsLaunchUri(uri: string, failCb: Fn, successCb: Fn) {
    navigator.msLaunchUri(uri, successCb, failCb)
}

function checkBrowser() {
    const isOpera = !!(window as any).opera || navigator.userAgent.indexOf(' OPR/') >= 0
    var ua = navigator.userAgent.toLowerCase()
    return {
        isOpera: isOpera,
        // @ts-ignore
        isFirefox: typeof InstallTrigger !== 'undefined',
        isSafari:
            (~ua.indexOf('safari') && !~ua.indexOf('chrome')) ||
            Object.prototype.toString.call((window as any).HTMLElement).indexOf('Constructor') > 0,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream,
        isChrome: !!(window as any).chrome && !isOpera,
        isIE: /*@cc_on!@*/ false || !!(document as any).documentMode, // At least IE6
    }
}

function getInternetExplorerVersion() {
    let rv = -1
    const ua = navigator.userAgent

    if (navigator.appName === 'Microsoft Internet Explorer') {
        const re = new RegExp('MSIE ([0-9]{1,}[.0-9]{0,})')
        if (re.exec(ua) != null) rv = parseFloat(RegExp.$1)
    } else if (navigator.appName === 'Netscape') {
        const re = new RegExp('Trident/.*rv:([0-9]{1,}[.0-9]{0,})')
        if (re.exec(ua) != null) {
            rv = parseFloat(RegExp.$1)
        }
    }
    return rv
}

function openUriUsingIEInOlderWindows(uri: string, failCb: Fn, successCb: Fn) {
    if (getInternetExplorerVersion() === 10) {
        openUriUsingIE10InWindows7(uri, failCb, successCb)
    } else if (getInternetExplorerVersion() === 9 || getInternetExplorerVersion() === 11) {
        openUriWithHiddenFrame(uri, failCb, successCb)
    } else {
        openUriInNewWindowHack(uri, failCb, successCb)
    }
}

export default function(uri: string, failCb: Fn, successCb: Fn, unsupportedCb?: Fn) {
    function failCallback() {
        failCb && failCb()
    }

    function successCallback() {
        successCb && successCb()
    }

    if (navigator.msLaunchUri) {
        //for IE and Edge in Win 8 and Win 10
        openUriWithMsLaunchUri(uri, failCb, successCb)
    } else {
        const browser = checkBrowser()

        if (browser.isFirefox) {
            openUriUsingFirefox(uri, failCallback, successCallback)
        } else if (browser.isChrome || browser.isIOS) {
            openUriWithTimeoutHack(uri, failCallback, successCallback)
        } else if (browser.isIE) {
            openUriUsingIEInOlderWindows(uri, failCallback, successCallback)
        } else if (browser.isSafari) {
            openUriWithHiddenFrame(uri, failCallback, successCallback)
        } else {
            unsupportedCb && unsupportedCb()
        }
    }
}
